/* eslint-disable camelcase */
import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Divider,
  Grid,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  TextField,
} from '@nextiva/next-ui-components';
import { useQueryClient } from 'react-query';

import { validData } from 'utils/validators';

import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { SectionSaveCancel } from 'components/SectionSaveCancel';

import {
  useGetCompanyInfo,
  useGetLicenseList,
  useGetManagerOptions,
  useGetManagersInfo,
  useGetCompanyTaxId,
  useUpdateCompanyInfo,
  useUpdateCompanyTaxId,
  useSnackbar,
} from 'hooks';
import CompanyLicenses from './CompanyLicenses';
import { RenderCompanyTaxId } from './RenderCompanyTaxId';
import ChimeIdentification from './ChimeIdentification';
import { CompanyInfoShadow } from './CompanyShadow';
import UpdateContactModal from './UpdateContactModel';

/**
 * AccountInfo: display and edit basic company information
 */
const CompanyInfo = () => {
  const {
    featureState: { accountInfoTaxID },
  } = useContext(FeatureContext);
  const queryClient = useQueryClient();
  const managerDetails = useGetManagersInfo();
  const managerOptions = useGetManagerOptions();
  const companyInfo = useGetCompanyInfo();
  const licenseInfo = useGetLicenseList();
  const companyTaxId = useGetCompanyTaxId();
  const updateCompanyInfo = useUpdateCompanyInfo();
  const updateCompanyTaxId = useUpdateCompanyTaxId();

  // payload is the shape of the updateCompany request body
  const [payload, setPayload] = useState({
    company_name:
      companyInfo?.data?.company_name ?? companyInfo?.data?.companyName,
    first_name: '',
    fiscal_year_start_date: '', // TODO: does the payload need this key?
    last_name: '',
    tax_data: '',
    tax_id_obj: {
      tax_id: '',
      tax_re_id: '',
    },
    is_cancel_btn_pressed: false,
  });

  // managerPayload is the shape of the updateContact request body
  const [managerPayload, setManagerPayload] = useState({
    firstName: '',
    lastName: '',
    locationId: '',
    userType: 'USER',
    accountNumber: '',
  });
  const [primaryContact, setPrimaryContact] = useState('');
  const [tempContact, setTempContact] = useState('');
  const [options, setOptions] = useState([]);
  const [isContactAdmin, setIsContactAdmin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [detailsOptions, setDetailsOptions] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [validation, setValidation] = useState({
    companyName: '',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const setSnackbar = useSnackbar();

  const toggleUpdateContactModal = useCallback(
    (_event, reason) => {
      // persist the modal if the promise is loading
      // and the user clicks outside of the modal.
      if (isLoading && reason === 'backdropClick') return;
      setIsModalOpen(!isModalOpen);
      setTempContact('');
    },
    [isModalOpen, isLoading],
  );

  function updateManagerOptions(first_name, last_name, managerOpts) {
    let exist = false;
    const managers = managerOpts;

    setOptions(managers);

    managerOpts.forEach((m) => {
      if (m.label === `${first_name} ${last_name}`) {
        setPrimaryContact(m.value);
        exist = true;
      }
    });

    if (!exist) {
      setIsContactAdmin(false);
      setOptions(
        managers.concat({
          label: `${first_name} ${last_name}`,
          value: 'default',
        }),
      );
      setPrimaryContact('default');
    }
  }

  useEffect(() => {
    if (!companyInfo.data) return;
    const {
      company_name,
      companyName,
      account_manager: { contact: { first_name, last_name } = {} } = {},
      accountManager: { contact: { firstName, lastName } = {} } = {},
    } = companyInfo.data;

    setPayload({ ...payload, company_name: company_name ?? companyName });
    setPrimaryContact(`${first_name ?? firstName} ${last_name ?? lastName}`);
    setDetailsOptions(managerDetails.data);
    updateManagerOptions(
      first_name ?? firstName,
      last_name ?? lastName,
      managerOptions,
    );
  }, [companyInfo.data, managerDetails.data]);

  useEffect(() => {
    if (companyTaxId?.data) {
      setPayload({
        ...payload,
        tax_data: companyTaxId.data,
      });
    } else if (companyTaxId?.error?.errorCode) {
      setPayload({
        ...payload,
        tax_data: '',
        is_cancel_btn_pressed: true,
      });
    }
  }, [companyTaxId?.isLoading]);

  useEffect(() => {
    if (licenseInfo.isError) {
      setSnackbar({
        message: 'Failed to get license information.',
        severity: 'error',
      });
    }
  }, [licenseInfo.isError]);

  const companyNameHandler = (e) => {
    setHasUnsavedChanges(true);
    setPayload({ ...payload, company_name: e.target.value });
    setValidation({
      ...validation,
      companyName: validData(100, e.target.value),
    });
  };

  function updateManagerPayload(uIdSelected) {
    detailsOptions.forEach((mg) => {
      if (mg.userUuid === uIdSelected) {
        setManagerPayload({
          ...managerPayload,
          firstName: `${mg.displayName.split(` `)[0]}`,
          lastName: `${mg.displayName.split(` `)[1]}`,
          locationId: mg.locationId,
          accountNumber: mg.corpAcctNbr,
        });
      }
    });
  }

  const primaryContactHandler = (e) => {
    setIsModalOpen(true);
    setTempContact(e.target.value);
    updateManagerPayload(e.target.value);
  };

  const doUpdateContact = (accountManagerUUID) => {
    if (!isContactAdmin) {
      setIsContactAdmin(true);
      setOptions(managerOptions);
    }
    setIsLoading(false);
    setPrimaryContact(accountManagerUUID);
    setTempContact('');
    setIsModalOpen(false);

    const foundItem = managerOptions.find(
      ({ userUuid }) => userUuid === accountManagerUUID,
    );
    const [first_name, last_name] = foundItem.label.split(' ');

    queryClient.setQueryData('companyInfo', (old) => ({
      ...old,
      accountManagerUUID,
      account_manager: {
        ...(old.account_manager ?? old.accountManager),
        contact: {
          ...(old.account_manager ?? old.accountManager).contact,
          first_name,
          last_name,
        },
      },
    }));
  };

  const onCancelHandle = () => {
    setHasUnsavedChanges(false);
    const oldTaxId = companyTaxId.data;
    const oldCompanyName =
      companyInfo.data.company_name || companyInfo.data.companyName;

    setPayload({
      ...payload,
      company_name: oldCompanyName,
      tax_id_obj: {
        tax_id: '',
        tax_re_id: '',
      },
      tax_data: oldTaxId,
      is_cancel_btn_pressed: !!companyTaxId?.error,
    });
  };

  const onSaveHandle = async () => {
    let newTaxId = companyTaxId.data;
    const taxidUpdated =
      payload.tax_id_obj.tax_id.length === 9 &&
      payload.tax_id_obj.tax_id !== companyTaxId.data;
    const companyNameUpdated =
      payload.company_name !== companyInfo.data.company_name ||
      payload.company_name !== companyInfo.data.companyName;
    const successMessage =
      taxidUpdated && !companyNameUpdated
        ? 'Your Tax Id number has been saved successfully!'
        : 'Your Account update has been saved successfully!';

    if (taxidUpdated) {
      await updateCompanyTaxId.mutate(
        { taxId: payload.tax_id_obj.tax_id },
        {
          onSuccess: (response) => {
            newTaxId = response.lastDigits;
            companyTaxId.refetch();
            setPayload({
              ...payload,
              tax_data: newTaxId,
              is_cancel_btn_pressed: false,
              tax_id_obj: {
                tax_id: '',
                tax_re_id: '',
              },
            });
          },
          onError: () => {
            setSnackbar({
              open: true,
              severity: 'error',
              message: 'The TAX ID could not be updated.',
            });
          },
        },
      );
    }

    await updateCompanyInfo.mutate(payload, {
      onSuccess: () => {
        setHasUnsavedChanges(false);
        setSnackbar({
          open: true,
          severity: 'success',
          message: successMessage,
        });
        queryClient.invalidateQueries(['companyInfo']);
      },
      onError: () => {
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'Company profile could not be updated.',
        });
      },
    });
  };

  return (
    <div className="pa-acc-info">
      <div className="pa-acc-info__form">
        <Grid item>
          <div
            className="pa-acc-info__title"
            data-testid="PlatformAdminUI-AccountInfo-CompanyInfo-Title"
          >
            Company Information
          </div>
          <div
            className="pa-acc-info__label"
            data-testid="PlatformAdminUI-AccountInfo-CompanyInfo-CompanyNameLabel"
          >
            COMPANY NAME
          </div>
          {companyInfo.isLoading ? (
            <CompanyInfoShadow className="pa-acc-info__text-field-shadow" />
          ) : (
            <TextField
              id="company_name"
              onChange={companyNameHandler}
              value={payload.company_name}
              helperText={
                !!validation.companyName &&
                `Company name ${validation.companyName}`
              }
              error={!!validation.companyName}
              inputProps={{
                'data-testid':
                  'PlatformAdminUI-AccountInfo-CompanyInfo-CompanyName',
              }}
              data-testid="PlatformAdminUI-AccountInfo-CompanyInfo-CompanyName"
            />
          )}
          <Divider />
        </Grid>
        <Grid item>
          <CompanyLicenses />
          <Divider />
        </Grid>
        {accountInfoTaxID && (
          <>
            <RenderCompanyTaxId
              companyTaxId={companyTaxId}
              setPristine={(pristine) => setHasUnsavedChanges(!pristine)}
              payload={payload}
              setPayload={setPayload}
            />
            <Divider />
          </>
        )}

        <Grid item data-testid="Chime Grid Item">
          <ChimeIdentification />
          {companyInfo.data?.amazonTeamAccountId && <Divider />}
        </Grid>
        <Grid item>
          <div
            className="pa-acc-info__title pa-acc-info__title--contacts"
            data-testid="PlatformAdminUI-AccountInfo-CompanyInfo-CompanyContactsTitle"
          >
            Company Contacts
          </div>
          <div
            className="pa-acc-info__sub-text"
            data-testid="PlatformAdminUI-AccountInfo-CompanyInfo-CompanyContactsText"
          >
            Your contact will get important updates from Nextiva related to your
            account, billing, and support issues.
          </div>
          <div
            className="pa-acc-info__label"
            data-testid="PlatformAdminUI-AccountInfo-CompanyInfo-PrimaryContactLabel"
          >
            PRIMARY CONTACT (REQUIRED)
          </div>
          {companyInfo.isLoading ? (
            <CompanyInfoShadow className="pa-acc-info__text-field-shadow" />
          ) : (
            <FormControl>
              <InputLabel id="Select-Option">Select an option</InputLabel>
              <Select
                labelId="Select-Option"
                onChange={primaryContactHandler}
                value={tempContact || primaryContact}
                id="preferred_manager"
                data-testid="PlatformAdminUI-AccountInfo-CompanyInfo-SelectOption"
              >
                {options.map((option) => (
                  <MenuItem key={option.label} value={option.userUuid}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {isModalOpen && (
            <UpdateContactModal
              toggleModal={toggleUpdateContactModal}
              isLoading={isLoading}
              isModalOpen={isModalOpen}
              updatePrimaryContact={doUpdateContact}
              tempContact={tempContact}
              managerPayload={managerPayload}
              setIsLoading={setIsLoading}
              setIsModalOpen={setIsModalOpen}
            />
          )}
        </Grid>
        <Grid className="pa-acc-info__actions" item>
          <SectionSaveCancel
            open={hasUnsavedChanges}
            cancelOnClick={onCancelHandle}
            saveOnClick={onSaveHandle}
          />
        </Grid>
      </div>
    </div>
  );
};

export default CompanyInfo;
